import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ChevronDownIcon } from '../components/ui/icons';
import './PageStyles.css';

interface FaqEntry {
  /** Also the URL fragment — /about#what-is-this opens that answer. */
  id: string;
  question: string;
  answer: React.ReactNode;
}

const FAQS: FaqEntry[] = [
  {
    id: 'no-qr-code',
    question: "I can't find the QR code in a pub",
    answer: (
      <>
        <p>
          Our <a href="/example-qr-1.jpeg" target="_blank" rel="noopener noreferrer">
             QR codes look like this
          </a>{' '}
             and are framed on the wall of the Pub or Bar so have a good look around or if you still can't spot it, ask a member of bar staff.
        </p>
        <p>
          If you think our QR code is missing or it's been damaged please contact us
        </p>
      </>
    ),
  },
  {
    id: 'lost-crawl',
    question: "I've lost my crawl",
    answer: (
      <>
        <p>
          We use cookies to remember your crawl in your web browser. 
          If your progress has disappeared, check that you're using the same browser and haven't cleared your browsing data.
        </p>
        <p>
          If you've switched phone or browser, you can restore your crawl using your{' '}
          <Link to="/#share">share code or link</Link>{' '}
          (provided it's not been more than 12-hours since you scanned the first QR code)
        </p>
        <p>Unfortunately if your browsing data was cleared and you don't have your sharing code your session cannot be restored</p>
      </>
    ),
  },
  {
    id: 'wrong-pub',
    question: 'I scanned the wrong pub or scanned the same pub twice',
    answer: (
      <>
        <p>
          You can scan any pub on the same crawl in <strong>any order</strong>. 
        </p>
          <p>If you scanned the same QR code twice: no harm done: 
          we'll save the time from your first scan only.
        </p>
        <p>
          If you scan a pub that belongs to a <strong>different crawl</strong>, we'll ask you before anything
          changes — choose <strong>Stay on my crawl</strong> and your current crawl carries on
          untouched or you can choose to end your crawl and start fresh on the new one.
        </p>
      </>
    ),
  },
  {
    id: 'team-scans',
    question: "Can we do the crawl as a team?",
    answer: (
      <>
      <p>
        You can add people to your crawl by giving them your {' '}
          <Link to="/#share">share code or link</Link>{' '}
      </p>
      <p>
        This will put everyone into the same session, as a team you can set your team name and you'll all share the same time.
      </p>
      <p>
        A scan for 1 is a scan for all: once one person has scanned a QR code it should appear on everyone's device (you may need to refresh!)
      </p>
      </>
    ),
  },
  {
    id: 'what-is-this',
    question: 'What is this website, who made it?',
    answer: (
      <>
        <p>
          Scan-Drink-Race.com is an innovative way to discover, track and compete on your
          favourite pub crawls. To start, find one of our unique{' '}
          <a href="/example-qr-1.jpeg" target="_blank" rel="noopener noreferrer">
            Pub QR codes
          </a>{' '}
          in a participating pub. Simply scan it using your smartphone and scan-drink-race.com will
          launch in your phone's web browser - no logins or downloads required!
        </p>
        <p>
          We'll show you the other pubs connected on your current crawl, track your progress and
          you can even post your final time to our leaderboard for ultimate bragging rights! All
          you have to do is scan the QR codes you'll find in each pub along the way (and try not to
          get too drunk). Happy drinking and enjoy!
        </p>
        <p>
          <em>Created with ❤️ (and 🍺) by Sawley Shuffle Ltd.</em>
        </p>
      </>
    ),
  },
];

const About: React.FC = () => {
  const { hash } = useLocation();
  const [openId, setOpenId] = useState<string | null>(null);

  // A fragment (e.g. the Rules sheet's /about#what-is-this link) opens and
  // reveals that answer rather than dropping the reader on a page of headings.
  useEffect(() => {
    const id = hash.replace('#', '');
    if (!id || !FAQS.some(faq => faq.id === id)) return;
    setOpenId(id);
    document.getElementById(`faq-${id}`)?.scrollIntoView({ block: 'center' });
  }, [hash]);

  return (
    <div className="page-container">
      <div className="page-content">
        <h1 className="page-title" data-testid="page-title">
          Help and FAQs
        </h1>

        <div className="faq-list">
          {FAQS.map(faq => {
            const isOpen = openId === faq.id;
            return (
              <div className="faq-item" key={faq.id} id={`faq-${faq.id}`}>
                <button
                  type="button"
                  className="faq-question"
                  aria-expanded={isOpen}
                  aria-controls={`faq-answer-${faq.id}`}
                  onClick={() => setOpenId(current => (current === faq.id ? null : faq.id))}
                  data-testid={`faq-toggle-${faq.id}`}
                >
                  <span>{faq.question}</span>
                  <ChevronDownIcon className="faq-chev" />
                </button>
                {isOpen && (
                  <div
                    className="faq-answer"
                    id={`faq-answer-${faq.id}`}
                    data-testid={`faq-answer-${faq.id}`}
                  >
                    {faq.answer}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default About;
